import React, { useState } from "react";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { ArrowUpIcon } from "@radix-ui/react-icons";
import { RequestEmailMessage } from "../../entities/Message";
import { MessageAvatarSVG } from "../loadSVG";
import { SvgRadius } from "../../entities/ChatSettingsV2";
import { MessageText } from "./MessageText";
import { validateEmail } from "../../utils/validation";

// Radix UI imports
import * as Form from "@radix-ui/react-form";

dayjs.extend(relativeTime);

const PRIORITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

const PRODUCT_VERSION_OPTIONS = [
  {
    id: 35855007993371,
    name: "Dremio 26.0..x",
    raw_name: "Dremio 26.0..x",
    value: "ticket_dremio_26.0..x",
    default: false,
  },
  {
    id: 30448313736219,
    name: "Dremio 25.2.x",
    raw_name: "Dremio 25.2.x",
    value: "ticket_dremio_25.2.x",
    default: false,
  },
  {
    id: 29345059071387,
    name: "Dremio 25.1.x",
    raw_name: "Dremio 25.1.x",
    value: "ticket_dremio_25.1.x",
    default: false,
  },
  {
    id: 29345059071515,
    name: "Dremio 25.0.x",
    raw_name: "Dremio 25.0.x",
    value: "ticket_dremio_25.0.x",
    default: false,
  },
  {
    id: 24752469379995,
    name: "Dremio 25.x",
    raw_name: "Dremio 25.x",
    value: "ticket_dremio_25.x",
    default: false,
  },
  {
    id: 7018464433947,
    name: "Dremio Cloud",
    raw_name: "Dremio Cloud",
    value: "ticket_dremio_cloud",
    default: false,
  },
  {
    id: 29345059071643,
    name: "Dremio 24.3.x (EOL)",
    raw_name: "Dremio 24.3.x (EOL)",
    value: "ticket_dremio_24.3.x",
    default: false,
  },
  {
    id: 29345059071771,
    name: "Dremio 24.2.x (EOL)",
    raw_name: "Dremio 24.2.x (EOL)",
    value: "ticket_dremio_24.2.x",
    default: false,
  },
  {
    id: 29345059071899,
    name: "Dremio 24.1.x (EOL)",
    raw_name: "Dremio 24.1.x (EOL)",
    value: "ticket_dremio_24.1.x",
    default: false,
  },
  {
    id: 29345059072027,
    name: "Dremio 24.0.x (EOL)",
    raw_name: "Dremio 24.0.x (EOL)",
    value: "ticket_dremio_24.0.x",
    default: false,
  },
  {
    id: 12938967490203,
    name: "Dremio 24.x (EOL)",
    raw_name: "Dremio 24.x (EOL)",
    value: "ticket_dremio_24.x",
    default: false,
  },
  {
    id: 9587016900891,
    name: "Dremio 23.x (EOL)",
    raw_name: "Dremio 23.x (EOL)",
    value: "ticket_dremio_23.x",
    default: false,
  },
  {
    id: 7750758089115,
    name: "Dremio 22.x (EOL)",
    raw_name: "Dremio 22.x (EOL)",
    value: "ticket_dremio_22.x",
    default: false,
  },
  {
    id: 7018464434075,
    name: "Dremio 21.x (EOL)",
    raw_name: "Dremio 21.x (EOL)",
    value: "ticket_dremio_21.x",
    default: false,
  },
  {
    id: 7018464435995,
    name: "Other Version (EOL)",
    raw_name: "Other Version (EOL)",
    value: "ticket_version_other",
    default: false,
  },
  {
    id: 7018464436123,
    name: "N/A",
    raw_name: "N/A",
    value: "ticket_version_n/a",
    default: false,
  },
];

const ENVIRONMENT_OPTIONS = [
  {
    id: 1260822158230,
    name: "Production",
    raw_name: "Production",
    value: "enviro_production",
    default: false,
  },
  {
    id: 1260822158250,
    name: "Development",
    raw_name: "Development",
    value: "enviro_development",
    default: false,
  },
  {
    id: 1260822158270,
    name: "Staging",
    raw_name: "Staging",
    value: "enviro_staging",
    default: false,
  },
  {
    id: 1260822158290,
    name: "UAT",
    raw_name: "UAT",
    value: "enviro_uat",
    default: false,
  },
  {
    id: 1260822158310,
    name: "Other Deployment",
    raw_name: "Other Deployment",
    value: "enviro_other_deployment",
    default: false,
  },
  {
    id: 1260822232850,
    name: "N/A",
    raw_name: "N/A",
    value: "enviro_n/a",
    default: false,
  },
];

const PRODUCT_AREA_OPTIONS = [
  {
    id: 32469322856987,
    name: "Core Dremio, Configuration, and Upgrades ",
    raw_name: "Core Dremio, Configuration, and Upgrades ",
    value: "product_core_dremio",
    default: true,
  },
  {
    id: 32469322857115,
    name: "SQL, Dremio RBAC, Reflections and Metadata",
    raw_name: "SQL, Dremio RBAC, Reflections and Metadata",
    value: "product_sql_rbac_reflections_metadata",
    default: false,
  },
  {
    id: 32469322857243,
    name: "Query Performance Issues",
    raw_name: "Query Performance Issues",
    value: "product_query_performance",
    default: false,
  },
  {
    id: 32469322857371,
    name: "BI Connectors",
    raw_name: "BI Connectors",
    value: "product_bi_connectors",
    default: false,
  },
  {
    id: 32469322857499,
    name: "RDBMS Sources",
    raw_name: "RDBMS Sources",
    value: "product_rdbms_sources",
    default: false,
  },
  {
    id: 32747633281819,
    name: "Kubernetes or Platform",
    raw_name: "Kubernetes or Platform",
    value: "product_kubernetes_or_platform",
    default: false,
  },
  {
    id: 32469322857627,
    name: "Hadoop Sources and issues",
    raw_name: "Hadoop Sources and issues",
    value: "product_hadoop_sources",
    default: false,
  },
  {
    id: 32469322857755,
    name: "NoSQL Sources",
    raw_name: "NoSQL Sources",
    value: "product_nosql_sources",
    default: false,
  },
  {
    id: 32469322857883,
    name: "Dremio Catalog ",
    raw_name: "Dremio Catalog ",
    value: "product_dremio_catalog",
    default: false,
  },
  {
    id: 32469322858011,
    name: "Security and Authentication",
    raw_name: "Security and Authentication",
    value: "product_security_and_authentication",
    default: false,
  },
  {
    id: 32469322858139,
    name: "API/ Programming",
    raw_name: "API/ Programming",
    value: "product_api_and_programming",
    default: false,
  },
  {
    id: 32469322858267,
    name: "Account Administration",
    raw_name: "Account Administration",
    value: "product_account_administration",
    default: false,
  },
  {
    id: 32469322858395,
    name: "Other",
    raw_name: "Other",
    value: "product_other",
    default: false,
  },
];

const DEPLOYMENT_TYPE_OPTIONS = [
  {
    id: 1260822158090,
    name: "Dremio Cloud",
    raw_name: "Dremio Cloud",
    value: "deploy_dremio_cloud",
    default: false,
  },
  {
    id: 1260822158030,
    name: "AWS Marketplace (AWS Edition)",
    raw_name: "AWS Marketplace (AWS Edition)",
    value: "deploy_awse",
    default: false,
  },
  {
    id: 1260822158070,
    name: "Azure ARM",
    raw_name: "Azure ARM",
    value: "deploy_azure_arm",
    default: false,
  },
  {
    id: 1260822158010,
    name: "AWS EKS",
    raw_name: "AWS EKS",
    value: "deploy_aws_eks",
    default: false,
  },
  {
    id: 1260822158050,
    name: "Azure AKS",
    raw_name: "Azure AKS",
    value: "deploy_azure_aks",
    default: false,
  },
  {
    id: 7236932261275,
    name: "Google Cloud GKE",
    raw_name: "Google Cloud GKE",
    value: "google_cloud_gke",
    default: false,
  },
  {
    id: 1260822158130,
    name: "Hadoop/YARN",
    raw_name: "Hadoop/YARN",
    value: "deploy_hadoop/yarn",
    default: false,
  },
  {
    id: 1260822158150,
    name: "MapR/YARN",
    raw_name: "MapR/YARN",
    value: "deploy_mapr/yarn",
    default: false,
  },
  {
    id: 1260822158170,
    name: "On-Prem",
    raw_name: "On-Prem",
    value: "deploy_on-prem",
    default: false,
  },
  {
    id: 31527858393371,
    name: "N/A",
    raw_name: "N/A",
    value: "deploy_n/a",
    default: false,
  },
];

export function RequestEmailFormComponent({
  text,
  customSVG,
  svgRadius,
  data: { defaultEmail = "" },
  isLastMessage,
  onEscalationEmail,
  showAvatar,
  createdAt,
  showTimestamp,
  chatEnded,
}: RequestEmailMessage & {
  customSVG: string;
  svgRadius?: SvgRadius;
  isLastMessage: boolean;
  onEscalationEmail: (email: string, additionalData: any) => Promise<void>;
  showAvatar: boolean;
  showTimestamp: boolean;
  chatEnded: boolean;
}) {
  const [email, setEmail] = useState(defaultEmail);
  const [subject, setSubject] = useState("");
  const [priority, setPriority] = useState("normal");
  const [productVersion, setProductVersion] = useState("");
  const [environment, setEnvironment] = useState("");
  const [productArea, setProductArea] = useState("");
  const [deploymentType, setDeploymentType] = useState("");
  const [loading, setLoading] = useState(false);
  const [showInput, setShowInput] = useState(isLastMessage);
  const [emailError, setEmailError] = useState<string>("");
  const [subjectError, setSubjectError] = useState<string>("");

  async function handleSubmitForm(e: React.FormEvent) {
    e.preventDefault();
    setEmailError("");
    setSubjectError("");

    const validation = validateEmail(email);
    let hasError = false;
    if (!validation.isValid) {
      setEmailError(validation.error!);
      hasError = true;
    }
    if (!subject.trim()) {
      setSubjectError("Subject is required.");
      hasError = true;
    }
    if (hasError) return;

    setLoading(true);
    try {
      // you can expand the payload as needed
      await onEscalationEmail(email.trim(), {
        subject: subject.trim(),
        priority: priority,
        custom_fields: [
          {
            id: 1900003173044,
            value: productVersion,
          },
          {
            id: 1260826363170,
            value: environment,
          },
          {
            id: 32469322858907,
            value: productArea,
          },
          {
            id: 1260826362790,
            value: deploymentType,
          },
        ],
      });
      setShowInput(false);
    } catch (error) {
      setEmailError("Failed to send form. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  // Remove Form.Control asChild for <select> fields to fix select not working
  // See: https://github.com/radix-ui/primitives/issues/1866

  return (
    <div
      style={{
        paddingLeft: showAvatar ? 0 : 36,
        display: "flex",
        gap: "12px",
        alignItems: "flex-start",
      }}
    >
      {showAvatar && (
        <MessageAvatarSVG path={customSVG} radius={svgRadius} alt={"Chat Message Avatar"} />
      )}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "8px",
          width: "100%",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            width: "100%",
            background:
              "linear-gradient(260.74deg, rgba(249, 243, 227, 0.35) 4.89%, rgba(226, 234, 255, 0.35) 101.01%)",
            padding: "8px 12px",
            borderRadius: "8px",
            maxWidth: "340px",
          }}
        >
          <div style={{ paddingBottom: "16px" }}>
            <MessageText text={text} />
          </div>
          {showInput && !chatEnded && (
            <Form.Root
              onSubmit={handleSubmitForm}
              style={{
                width: "100%",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              <Form.Field name="email">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <Form.Label
                    htmlFor="email"
                    style={{
                      fontSize: "12px",
                      fontWeight: 500,
                      marginBottom: "4px",
                    }}
                  >
                    Email
                  </Form.Label>
                  <Form.Control asChild>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        if (emailError) setEmailError("");
                      }}
                      placeholder="Enter your email"
                      disabled={loading}
                      autoComplete="email"
                      style={{
                        width: "100%",
                        borderRadius: "6px",
                        border: `1px solid ${emailError ? "#ef4444" : "#d1d5db"}`,
                        padding: "8px 12px",
                        fontSize: "14px",
                        background: "#fff",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                  </Form.Control>
                  {emailError && (
                    <span style={{ fontSize: "12px", color: "#ef4444" }}>{emailError}</span>
                  )}
                </div>
              </Form.Field>
              <Form.Field name="subject">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <Form.Label
                    htmlFor="subject"
                    style={{
                      fontSize: "12px",
                      fontWeight: 500,
                      marginBottom: "4px",
                    }}
                  >
                    Subject
                  </Form.Label>
                  <Form.Control asChild>
                    <input
                      id="subject"
                      type="text"
                      value={subject}
                      onChange={(e) => {
                        setSubject(e.target.value);
                        if (subjectError) setSubjectError("");
                      }}
                      placeholder="Enter subject"
                      disabled={loading}
                      style={{
                        width: "100%",
                        borderRadius: "6px",
                        border: `1px solid ${subjectError ? "#ef4444" : "#d1d5db"}`,
                        padding: "8px 12px",
                        fontSize: "14px",
                        background: "#fff",
                        outline: "none",
                        boxSizing: "border-box",
                      }}
                    />
                  </Form.Control>
                  {subjectError && (
                    <span style={{ fontSize: "12px", color: "#ef4444" }}>{subjectError}</span>
                  )}
                </div>
              </Form.Field>
              <Form.Field name="priority">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <Form.Label
                    htmlFor="priority"
                    style={{
                      fontSize: "12px",
                      fontWeight: 500,
                      marginBottom: "4px",
                    }}
                  >
                    Priority
                  </Form.Label>
                  {/* Remove Form.Control asChild for select */}
                  <select
                    id="priority"
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    disabled={loading}
                    style={{
                      width: "100%",
                      borderRadius: "6px",
                      border: "1px solid #d1d5db",
                      padding: "8px 12px",
                      fontSize: "14px",
                      background: "#fff",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  >
                    {PRIORITY_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </Form.Field>
              <Form.Field name="productVersion">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <Form.Label
                    htmlFor="productVersion"
                    style={{
                      fontSize: "12px",
                      fontWeight: 500,
                      marginBottom: "4px",
                    }}
                  >
                    Product Version
                  </Form.Label>
                  {/* Remove Form.Control asChild for select */}
                  <select
                    id="productVersion"
                    value={productVersion}
                    onChange={(e) => setProductVersion(e.target.value)}
                    disabled={loading}
                    style={{
                      width: "100%",
                      borderRadius: "6px",
                      border: "1px solid #d1d5db",
                      padding: "8px 12px",
                      fontSize: "14px",
                      background: "#fff",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  >
                    <option value="">Select version</option>
                    {PRODUCT_VERSION_OPTIONS.map((opt) => (
                      <option key={opt.id} value={opt.value}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                </div>
              </Form.Field>
              <Form.Field name="environment">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <Form.Label
                    htmlFor="environment"
                    style={{
                      fontSize: "12px",
                      fontWeight: 500,
                      marginBottom: "4px",
                    }}
                  >
                    Environment
                  </Form.Label>
                  {/* Remove Form.Control asChild for select */}
                  <select
                    id="environment"
                    value={environment}
                    onChange={(e) => setEnvironment(e.target.value)}
                    disabled={loading}
                    style={{
                      width: "100%",
                      borderRadius: "6px",
                      border: "1px solid #d1d5db",
                      padding: "8px 12px",
                      fontSize: "14px",
                      background: "#fff",
                      outline: "none",
                      boxSizing: "border-box",
                      maxWidth: "340px",
                    }}
                  >
                    <option value="">Select environment</option>
                    {ENVIRONMENT_OPTIONS.map((opt) => (
                      <option key={opt.id} value={opt.value}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                </div>
              </Form.Field>
              <Form.Field name="productArea">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <Form.Label
                    htmlFor="productArea"
                    style={{
                      fontSize: "12px",
                      fontWeight: 500,
                      marginBottom: "4px",
                    }}
                  >
                    Product Area
                  </Form.Label>
                  {/* Remove Form.Control asChild for select */}
                  <select
                    id="productArea"
                    value={productArea}
                    onChange={(e) => setProductArea(e.target.value)}
                    disabled={loading}
                    style={{
                      width: "100%",
                      borderRadius: "6px",
                      border: "1px solid #d1d5db",
                      padding: "8px 12px",
                      fontSize: "14px",
                      background: "#fff",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  >
                    <option value="">Select product area</option>
                    {PRODUCT_AREA_OPTIONS.map((opt) => (
                      <option key={opt.id} value={opt.value}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                </div>
              </Form.Field>
              <Form.Field name="deploymentType">
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                  }}
                >
                  <Form.Label
                    htmlFor="deploymentType"
                    style={{
                      fontSize: "12px",
                      fontWeight: 500,
                      marginBottom: "4px",
                    }}
                  >
                    Deployment Type
                  </Form.Label>
                  {/* Remove Form.Control asChild for select */}
                  <select
                    id="deploymentType"
                    value={deploymentType}
                    onChange={(e) => setDeploymentType(e.target.value)}
                    disabled={loading}
                    style={{
                      width: "100%",
                      borderRadius: "6px",
                      border: "1px solid #d1d5db",
                      padding: "8px 12px",
                      fontSize: "14px",
                      background: "#fff",
                      outline: "none",
                      boxSizing: "border-box",
                    }}
                  >
                    <option value="">Select deployment type</option>
                    {DEPLOYMENT_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.id} value={opt.value}>
                        {opt.name}
                      </option>
                    ))}
                  </select>
                </div>
              </Form.Field>
              <Form.Submit asChild>
                <button
                  type="submit"
                  disabled={loading}
                  onClick={handleSubmitForm}
                  style={{
                    marginTop: "8px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "9999px",
                    background: "#2563eb",
                    color: "#fff",
                    padding: "8px 16px",
                    fontWeight: 500,
                    fontSize: "15px",
                    border: "none",
                    cursor: loading ? "not-allowed" : "pointer",
                    opacity: loading ? 0.6 : 1,
                    transition: "background 0.2s",
                  }}
                  aria-label="Submit request email"
                >
                  <span style={{ marginRight: "8px" }}>Submit</span>
                  <ArrowUpIcon width="15" height="15" />
                  {loading && (
                    <span
                      style={{
                        marginLeft: "8px",
                        display: "inline-block",
                        border: "2px solid #fff",
                        borderTop: "2px solid transparent",
                        borderRadius: "50%",
                        width: "16px",
                        height: "16px",
                        animation: "spin 1s linear infinite",
                      }}
                    />
                  )}
                </button>
              </Form.Submit>
            </Form.Root>
          )}
        </div>
        {showTimestamp && (
          <span style={{ fontSize: "12px", color: "#6b7280" }}>
            {dayjs().diff(dayjs(createdAt), "day") > 1
              ? dayjs(createdAt).format("hh:mm:ss A")
              : dayjs(createdAt).fromNow()}
          </span>
        )}
      </div>
    </div>
  );
}
